<template lang="pug">
div
  q-dialog(v-model="show", transition-show="slide-left", transition-hide="slide-right")
    show-component(
      :rowId="rowId",
      :name="name",
      @close-show="closeShow",
      @open-print-act="openPrintAct",
      @open-edit="openEdit",
      @open-issue-form="openIssueForm",
      @destroy-table-item="destroyTableItem",
      @copy-row-item="copyRowItem",
      @show-checklist="openChecklistPreview"
    )

  q-dialog(
    :value="create",
    @input="onCreateDialogInput",
    @hide="resetRuntimeCustomCreate",
    transition-show="slide-left",
    transition-hide="slide-right"
  )
    component(
      v-if="customCreateComponent",
      ref="customCreateRef",
      :is="customCreateComponent",
      :key="customCreateKey",
      :class="customCreateComponentClass",
      :parentData="customFormParentData",
      @close-form="closeCustomCreate"
    )
    form-component.form-component(
      v-else,
      :uiPath="formPath",
      :path="createPath",
      :rowId="rowId",
      method="create",
      type="create",
      @reload-table="() => emit('reload')",
      @close-form="closeCreate"
    )

  q-dialog(:value="edit", @input="onEditDialogInput", transition-show="slide-left", transition-hide="slide-right")
    component(
      v-if="customEditComponent",
      ref="customEditRef",
      :is="customEditComponent",
      :parentData="customFormParentData",
      @close-form="closeCustomEdit",
      @row-is-active="() => {}"
    )
    form-component.form-component(
      v-else,
      :uiPath="formPath",
      :valuesPath="valuesPath",
      :rowId="rowId",
      method="patch",
      type="edit",
      @reload-table="() => emit('reload')",
      @close-form="closeEdit"
    )

  q-dialog(v-model="exportForm")
    form-component.export-form-component(
      :uiPath="exportExcelFormPath",
      :valuesPath="valuesPath",
      :reportClass="reportClass",
      type="export",
      @close-form="closeExport"
    )

  .print-pages(v-show="false")
    print-act-page(ref="printActRef", :parentData="{ data: completionCertificate }")
  checklist-passing-show(
    v-if="checklistPreviewData",
    :key="checklistPreviewKey",
    :parentData="checklistPreviewData",
    @close-show="closeChecklistPreview",
    @checklist-updated="onChecklistPreviewUpdated",
    @row-is-active="() => {}"
  )
</template>

<script setup>
import { ref, shallowRef, computed, nextTick } from "vue";
import showComponent from "../show/show.vue";
import printActPage from "../../show_card_components/print/print_completion_certificate.vue";
import printsCss from "../../../../assets/styles/prints/index";
import formComponent from "../form/form.vue";
import { openTabAndPrintContent } from "@/services/print";
import { useEmitter } from "@/services/useEmitter";
import checklistCreateForm from "../../../view/checklists/new.vue";
import checklistEditForm from "../../../view/checklists/edit.vue";
import checklistPassingShow from "../../../view/checklists/passing/show.vue";

const props = defineProps({
  /** Название текущей страницы. Нужно для вызовова нужного компонента. Пример: "acts" */
  name: { type: String, default: "" },
  /** id текущего ряда для его вызова. Если ряд не выбран приходит null. Пример: 1 */
  rowId: { type: null || Number, default: null },
  /** Класс дра репорта текущей страницы. Нужно для export формы */
  reportClass: { type: String, default: () => "" },
});

const emit = defineEmits([
  /** Эмит перезагрузки данных страницы без сброса фильтров. Не принимает параметры */
  "reload",
  /** Эмит для открытия формы создания заявки из обращения */
  "open-issue-form",
  /** Эмит удаления элемента таблицы. В качестве параметра принимает id элемента. Пример: 1 */
  "destroy-table-item",
  /** Эмит копирования элемента. В качестве параметра принимает id исходного элемента. Пример: 1 */
  "copy-row-item",
]);

const show = ref(false);
const edit = ref(false);
const create = ref(false);
const exportForm = ref(false);
const printActRef = ref(null);
const completionCertificate = ref({});
const exportFormLoaded = ref(false);
const customCreateRef = ref(null);
const customEditRef = ref(null);
const checklistPreviewData = ref(null);
const checklistPreviewKey = ref(0);
const runtimeCustomCreateComponent = shallowRef(null);
const runtimeCustomCreateParentData = ref(null);
const runtimeCustomCreateComponentClass = ref("");
const customCreateKey = ref(null);
const emitter = useEmitter();

const rowId = computed(() => props.rowId);
const formPath = computed(() => `/api/v3/ui/${props.name}/form`);
const createPath = computed(() => `/api/v3/${props.name}`);
const valuesPath = computed(() => `/api/v3/${props.name}/${rowId.value}`);
const exportExcelFormPath = computed(() => `/api/v3/ui/${props.name}/export_excel_form_data`);
const customForms = {
  checklists: {
    create: checklistCreateForm,
    edit: checklistEditForm,
  },
};
const customCreateComponent = computed(
  () => runtimeCustomCreateComponent.value ?? customForms[props.name]?.create ?? null,
);
const customCreateComponentClass = computed(() => runtimeCustomCreateComponentClass.value);
const customEditComponent = computed(() => customForms[props.name]?.edit ?? null);
const customFormParentData = computed(
  () =>
    runtimeCustomCreateParentData.value ?? {
      row: { id: rowId.value },
      grid: props.name,
    },
);

const openShow = async () => {
  // Дожидаемся обновления rowId от родителя перед показом карточки компонента
  await nextTick();

  if (rowId.value) {
    show.value = true;
  }
};

const openEdit = async (id = null) => {
  // Дожидаемся обновления rowId от родителя перед показом карточки компонента
  await nextTick();

  if (id) {
    rowId.value = id;
  }

  if (rowId.value) {
    edit.value = true;
  }
};

const openCreate = () => {
  create.value = true;
};

const openCustomCreate = ({ component, parentData = {}, key = null, componentClass = "" }) => {
  runtimeCustomCreateComponent.value = component;
  runtimeCustomCreateParentData.value = parentData;
  runtimeCustomCreateComponentClass.value = componentClass;
  customCreateKey.value = key;
  create.value = true;
};

const requestCustomCreateClose = (params = {}) => {
  if (!create.value) return;

  const request = {
    params,
    handled: false,
    markHandled() {
      this.handled = true;
    },
  };

  emitter.emit(`request-form-close:${customFormParentData.value.grid}`, request);

  if (!request.handled) {
    closeCustomCreate();
  }
};

const openIssueForm = id => {
  emit("open-issue-form", id);
};

const openPrintAct = async printData => {
  completionCertificate.value = printData;
  await nextTick();

  openTabAndPrintContent({
    html: printActRef.value.$el.innerHTML,
    css: printsCss.completion_certificate,
    closeModalFunction: undefined,
    popup: true,
  });
};

const normalizeApiPath = path => {
  if (!path) {
    return "";
  }

  return path.startsWith("/api/v3") ? path : `/api/v3${path}`;
};

const getIdFromDataUrl = path => {
  const id = String(path ?? "").match(/\/(\d+)(?:\?.*)?$/)?.[1];
  return id ? Number(id) : null;
};

const openChecklistPreview = ({ handler, row }) => {
  checklistPreviewData.value = {
    row: {
      ...(row ?? {}),
      id: getIdFromDataUrl(handler?.data_url) ?? row?.id ?? rowId.value ?? null,
    },
    grid: props.name,
    requestPath: normalizeApiPath(handler?.data_url),
  };
  checklistPreviewKey.value += 1;
};

const closeChecklistPreview = () => {
  checklistPreviewData.value = null;
};

const onChecklistPreviewUpdated = () => {
  closeChecklistPreview();
  emit("reload");
};

const destroyTableItem = id => {
  emit("destroy-table-item", id);
  closeShow();
};

const copyRowItem = id => {
  emit("copy-row-item", id);
};

const closeShow = () => {
  show.value = false;
};

const closeCreate = () => {
  create.value = false;
};

const closeEdit = () => {
  edit.value = false;
};

const openExport = () => {
  exportForm.value = true;
};

const closeExport = () => {
  exportForm.value = false;
};

const onCreateDialogInput = val => {
  if (val) {
    create.value = true;
    return;
  }

  if (runtimeCustomCreateComponent.value) {
    requestCustomCreateClose();
    return;
  }

  if (customCreateComponent.value) {
    customCreateRef.value?.requestClose?.();
    return;
  }

  closeCreate();
};

const onEditDialogInput = val => {
  if (val) {
    edit.value = true;
    return;
  }

  if (customEditComponent.value) {
    customEditRef.value?.requestClose?.();
    return;
  }

  closeEdit();
};

const shouldReloadAfterCustomFormClose = data => Boolean(data?.data || data?.method);

const closeCustomCreate = data => {
  if (shouldReloadAfterCustomFormClose(data)) {
    emit("reload");
  }

  closeCreate();
};

const resetRuntimeCustomCreate = () => {
  runtimeCustomCreateComponent.value = null;
  runtimeCustomCreateParentData.value = null;
  runtimeCustomCreateComponentClass.value = "";
  customCreateKey.value = null;
};

const closeCustomEdit = data => {
  if (shouldReloadAfterCustomFormClose(data)) {
    emit("reload");
  }

  closeEdit();
};

defineExpose({ openShow, openEdit, openCreate, openCustomCreate, openChecklistPreview, openExport });
</script>

<style scoped lang="scss">
.form-component {
  width: 650px;
  min-width: 650px;
  min-height: 100vh;
  position: absolute;
  top: 0;
  right: 0;
  padding: 16px;
  border-radius: 15px 0 0 15px !important;
  background: var(--show-card-background);
}

.export-form-component {
  width: 530px;
  max-height: 90vh;
  height: fit-content;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  padding: 16px;
  border-radius: 15px !important;
  background: var(--show-card-background);

  :deep(.form-data) {
    min-height: 208px;
  }

  :deep(.form-data .header .form-card-close-icon) {
    top: 25px !important;
    right: 25px !important;
    width: 12px;
  }
}
</style>
